import { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  selectUncheckedGroceryCount,
  selectGroceryWeekStart,
  buildGroceryList,
} from '../../redux/groceryListSlice';
import { selectCurrentWeekMeals, selectCurrentWeekStart } from '../../redux/mealPlanSlice';
import { isRecipeMeal } from '../../redux/mealEntry';
import { trackEvent } from '../../services/analyticsService';
import type { AppDispatch } from '../../redux/store';
import { buildGroceryBannerCopy } from './groceryBannerCopy';

/** Whole weeks from `from` to `to` (both Monday YYYY-MM-DD), signed. */
export const weekOffset = (from: string, to: string): number =>
  Math.round(
    (new Date(to + 'T12:00:00').getTime() - new Date(from + 'T12:00:00').getTime()) /
      (7 * 24 * 60 * 60 * 1000)
  );

export interface GroceryBannerState {
  /** Recipe meals on the week being viewed. Zero means: show nothing at all. */
  recipeMealCount: number;
  uncheckedCount: number;
  /** The grocery list belongs to a different week than the one on screen. */
  isOtherWeek: boolean;
  /** Everything the week needs is already in the pantry. */
  allCovered: boolean;
  /** One-line label — "38 à acheter pour 5 repas" / "Tout est au garde-manger". */
  label: string;
  /**
   * Second line for the surfaces that have room for one. Null when `label`
   * already carries the whole sentence, which is the case for the buy state.
   */
  sublabel: string | null;
  /** Opens the list, pinning it to the viewed week first when needed. */
  onPress: () => void;
}

/**
 * Shared state for every surface that shows the grocery count: the full-size
 * WeekCounterBanner, the compact mobile WeekSummaryLine, and the chip inside
 * the desktop NextMealBar.
 *
 * The rule worth not re-deriving per component: **browsing the planner must
 * never rewrite the grocery list.** Only an explicit tap pins the list to the
 * week on screen — see [[project_grocery_list_week_pinning]]. Until it does,
 * the surfaces offer "shop for this week" instead of a count that describes a
 * different week.
 */
export function useGroceryBannerState(): GroceryBannerState {
  const router = useRouter();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const uncheckedCount = useSelector(selectUncheckedGroceryCount);
  const weekMeals = useSelector(selectCurrentWeekMeals);
  const viewedWeekStart = useSelector(selectCurrentWeekStart);
  const groceryWeekStart = useSelector(selectGroceryWeekStart);

  const recipeMealCount = weekMeals.filter(isRecipeMeal).length;
  const isOtherWeek = groceryWeekStart !== viewedWeekStart;
  const allCovered = uncheckedCount === 0;

  const onPress = useCallback(() => {
    if (isOtherWeek) {
      trackEvent('grocery_week_pinned', {
        offset_weeks: weekOffset(groceryWeekStart, viewedWeekStart),
      });
      dispatch(buildGroceryList({ weekStart: viewedWeekStart }));
    }
    router.push('/(app)/grocery-list');
  }, [isOtherWeek, groceryWeekStart, viewedWeekStart, dispatch, router]);

  const { label, sublabel } = buildGroceryBannerCopy(t, {
    uncheckedCount,
    recipeMealCount,
    isOtherWeek,
    allCovered,
  });

  return {
    recipeMealCount,
    uncheckedCount,
    isOtherWeek,
    allCovered,
    label,
    sublabel,
    onPress,
  };
}
